import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { actionCreators } from './actions';
import { PromptConfirmation } from 'ui';
import { administrationEditTranslations } from '../constants';
import { AdministrationUserEditState } from './types-store';
import { ElementLoader } from 'ui/common/element-loader/element-loader';
import { safeNumericString, safeParam } from 'app/infrastructure/data/sanitize';
import { withSimpleForm } from 'app/infrastructure/simple-form';
import get from 'lodash/get';
import { serializeAccessData } from '../serialize';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { FormValuesType, SimpleFormProps } from 'app/infrastructure/simple-form/types';
import { withPrompt } from 'app/components/prompt-unsaved/prompt-unsaved';
import { AdministrationUserEditProps, UserEditSectionType } from './types';
import { AdminPaper } from 'app/area/administration/edit/components';
import { withOptions } from 'app/components/options-handler';
import { OverlayComponent } from 'app/components/overlay/overlay';
import { userEditFactory } from 'app/area/administration/edit/user/user-edit-factory';
import { compareAccess } from 'app/area/administration/edit/compare-access';
import { sfNames } from 'app/area/administration/edit/sf-names';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { UserEditForm } from 'app/area/administration/edit/user/user-edit-form';
import { routeConstants } from 'app/routes/route-constants';
import { metroLineConfiguration } from 'app/area/administration/edit/user/configuration';
import { RefObject } from 'react';
import { hasAccess } from './has-access';
import { PersonActionType } from 'app/area/administration/shared/types';
import { isVeracityEnabled } from 'app/infrastructure/hosting';
import { fetchingOptions, validationToBooleanValues } from 'app/area/administration/shared/helpers';
import { UserStickyHeader } from 'app/area/administration/edit/user/user-sticky-header';
import { getInvalidSection } from 'app/components/metro-line/helpers';
import { WithInvalidSection } from 'app/components/metro-line/types';
import { displayAssignRoleSection, excludeAccessRole } from 'app/area/administration/edit/user/helpers';
import { SpinLoader } from 'app/components';
import { shouldLoadRoles } from 'app/area/administration/list/filters/extra-info-filters';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type UserEditState = WithInvalidSection<UserEditSectionType>;

export class UserEdit extends React.Component<AdministrationUserEditProps, UserEditState> {
  public static statePath: string = STATE.administrationEdit;

  public state: Readonly<UserEditState> = {
    invalidSection: null
  };

  private detailsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private accessToOperationSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private accessToScreenSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private extendedAccessSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private restrictedAccessRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private accessToAdministrateUserGroupRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private accessToAssignRolesSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private defaultValuesSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private userOccurenceSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private notificationsRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private experienceTransferAlertsRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  public componentWillUnmount() {
    this.props.clearAllValues();
  }

  public componentDidMount() {
    const { id, params: { actionType }, sharedState: { access: { adminPanel: { user } } } } = this.props;
    scrollToTop();

    if (!this.props.sharedState.access.adminPanel || !hasAccess(user, actionType)) {
      navigate.toStartupView();
    } else {
      if (isDefinedIncludingZero(id)) {
        this.props.onLoadUser();
        this.props.loadUserAccess(id);
      }
      this.props.loadExtraInfo();
      this.props.loadPersonOptions();
      this.props.loadRolePanelAccess();
      actionType === PersonActionType.add && this.props.loadDefaultRole();
      shouldLoadRoles(this.props) && this.props.checkRoles();
    }
  }

  public componentDidUpdate(prevProps: AdministrationUserEditProps) {
    prevProps.accessPerson !== this.props.accessPerson && this.showUserInfoNotifications();
    (prevProps.simpleForm.editing.validation !== this.props.simpleForm.editing.validation ||
      prevProps.validation !== this.props.validation) &&
      this.state.invalidSection === undefined && this.updateInvalidSection();
    this.clearServerValidation(prevProps.formValues);
  }

  private showUserInfoNotifications = () => {
    const { hideDetails, showUserEditMenu, accessPerson: { dbAccess: { add = false, edit = false } = {} } = {}, translation,
      showUserInfoNotifications } = this.props;

    if (isVeracityEnabled()) {
      return;
    }

    showUserInfoNotifications([{
      show: showUserEditMenu && add,
      message: translation.database_create_user_info
    }, {
      show: edit && !hideDetails,
      message: translation.database_can_not_change_user_id_info
    }]);
  };

  private onSave = async () => {
    await this.props.onSave();

    this.updateInvalidSection();
  };

  private updateInvalidSection = () => {
    const { id, params: { actionType }, validation: serverValidation, hasRoles,
      sharedState: { access: { adminPanel: { restrictedAccessPanel } = {} } = {} } = {} } = this.props;

    const { simpleForm: {
      editing: { validation = {} } = {}
    } = {}, sharedState: {
      access: {
        adminPanel: {
          experienceTransferAlertsPanel
        }
      }
    } } = this.props;

    const configuration = metroLineConfiguration(
      id,
      actionType,
      hasRoles,
      restrictedAccessPanel,
      experienceTransferAlertsPanel,
      this.detailsSectionRef,
      this.accessToOperationSectionRef,
      this.accessToScreenSectionRef,
      this.extendedAccessSectionRef,
      this.restrictedAccessRef,
      this.accessToAdministrateUserGroupRef,
      this.accessToAssignRolesSectionRef,
      this.defaultValuesSectionRef,
      this.userOccurenceSectionRef,
      this.notificationsRef,
      this.experienceTransferAlertsRef,
      validationToBooleanValues({ ...validation, ...serverValidation })
    );

    const invalidSection = getInvalidSection(configuration);

    this.setState({ invalidSection });
  };

  private clearServerValidation = (prevFormValues: FormValuesType) => {
    const { formValues, validation, clearValidation } = this.props;
    const formIds = [sfNames.name, sfNames.personId, sfNames.accessRole];
    formIds.forEach(key => formValues[key] !== prevFormValues[key] && validation[key] && clearValidation(key));
  };

  // eslint:disable-next-line:max-func-body-length
  public render(): JSX.Element {
    const { invalidSection } = this.state;
    const { accessPerson, data, translation, fetchingLoadUser, fetchingSaveUser, formValues,
      role, validation: serverValidation, section, id, fetchingDefaultRole,
      showPersonId, params: { actionType }, options, hasRoles,
      sharedState: { access: { adminPanel: { restrictedAccessPanel } = {} } = {} } = {}
    } = this.props;
    const { sharedState: {
      access: {
        adminPanel: {
          experienceTransferAlertsPanel,
          extraInfoPanel = false,
          alternativeIdPanel = false
        } = {}
      } = {}
    } = {} } = this.props;

    const { simpleForm: {
      editing: { validation = {} } = {}
    } = {} } = this.props;

    const selectedUserType = get(formValues, `${sfNames.userType}.value`);

    const shouldRender = !fetchingLoadUser && ((!isDefinedIncludingZero(this.props.id) && !accessPerson) || (data && !!accessPerson)) &&
      !fetchingDefaultRole;

    if (!shouldRender) {
      return <ElementLoader />;
    }

    const scrollViewUrl = getScrollViewUrl(actionType, id);
    const optionsFetching = !!fetchingOptions(options);

    const configuration = metroLineConfiguration(
      id,
      actionType,
      displayAssignRoleSection(hasRoles, data?.accessToAssignRoles),
      restrictedAccessPanel,
      experienceTransferAlertsPanel,
      this.detailsSectionRef,
      this.accessToOperationSectionRef,
      this.accessToScreenSectionRef,
      this.extendedAccessSectionRef,
      this.restrictedAccessRef,
      this.accessToAdministrateUserGroupRef,
      this.accessToAssignRolesSectionRef,
      this.defaultValuesSectionRef,
      this.userOccurenceSectionRef,
      this.notificationsRef,
      this.experienceTransferAlertsRef,
      validationToBooleanValues({ ...validation, ...serverValidation })
    );

    return <div>
      {optionsFetching && <SpinLoader />}
      <OverlayComponent
        displayOverlay={fetchingSaveUser}
        overlayText={translation.save_user_load_overlay_text} />
      {this.props.shouldDisplayModal
        ? <PromptConfirmation
          title={translation.cancel_changes}
          description={translation.are_you_sure}
          continueText={translation.undo_all}
          cancelText={translation.go_back}
          onContinue={this.props.continue}
          onCancel={this.props.cancel}
        />
        : ''}
      {this.props.isTechnicalUserWarningShown
        ? <PromptConfirmation
          title={translation.technical_user_update}
          description={translation.technical_user_update_description}
          continueText={translation.technical_user_update}
          cancelText={translation.go_back}
          onContinue={this.onSave}
          onCancel={this.props.hideTechnicalUserWarning}
        />
        : ''}
      <AdminPaper>

        <UserStickyHeader {...this.props} onSave={this.onSave} optionsFetching={optionsFetching} />

        <UserEditForm
          {...this.props}
          invalidSection={invalidSection}
          validation={{ ...validation, ...serverValidation }}
          selectedUserType={selectedUserType as unknown as number}
          extraInfoPanel={extraInfoPanel}
          alternativeIdPanel={alternativeIdPanel}
          showPersonId={showPersonId}
          role={role}
          label={translation.user_administration_label}
          initialSection={section as UserEditSectionType}
          configuration={configuration}
          scrollViewUrl={scrollViewUrl}
        />
      </AdminPaper>
    </div>;
  }
}

const getScrollViewUrl = (actionType: PersonActionType, id: number) => {
  return actionType && {
    [PersonActionType.add]: routeConstants.userAddFor(PersonActionType.add),
    [PersonActionType.edit]: routeConstants.userEditFor(id),
    [PersonActionType.duplicate]: routeConstants.userDuplicateFor(id)
  }[actionType];
};

const inEditMode = (data, accessPerson) => (accessPerson && accessPerson.edit) || !data;

const selectors = {
  id: (state: AdministrationUserEditState, props: DefaultContainerProps) => safeNumericString(props.params.id),
  editMode: ({ data, accessPerson }: AdministrationUserEditState, props: DefaultContainerProps) => inEditMode(data, accessPerson),
  shouldPrompt: ({ data, accessPerson, accessData, extraInfoCodes, defaultRole }: AdministrationUserEditState,
    { params: { actionType } = {}, serialize, sharedState: { access: { adminPanel } } }: DefaultContainerProps & SimpleFormProps) =>
    inEditMode(data, accessPerson) && compareAccess(excludeAccessRole({
      ...serialize(defaultSerializeMappings),
      ...serializeAccessData(accessData, actionType, extraInfoCodes, data, adminPanel)
    }, defaultRole), data),
  section: (_: AdministrationUserEditState, props: DefaultContainerProps) => safeParam(props.params.section)
};

const container = withSimpleForm(containerFactory(withPrompt(withOptions(userEditFactory(UserEdit))),
  actionCreators, selectors, administrationEditTranslations));

export { container as UserEditView };
